import { useEffect, useState } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import DataSubmittedPopup from "../../utils/Popups/DataSubmittedPopup";
import useLeaveCountStore from "../../store/useLeaveCountStore";

const LeaveLogPopup = ({ user, setShowLeaveLog }) => {
  const accessToken = localStorage.getItem("accessToken");

  const [message, setMessage] = useState("");
  const [confirmationPopup, setConfirmationPopup] = useState(false);

  const { leaveCount, fetchLeaveCount, loading } = useLeaveCountStore();

  useEffect(() => {
    if (user?.id) {
      fetchLeaveCount(user.id);
    }
  }, [user?.id]);

  const handleSubmit = async (values, { setSubmitting }) => {
  try {
    // SEND MANUALLY ENTERED BALANCE
    const updatedData = values.leaveCounts
      .filter((leave) => {
        const original = leaveCount.find(
          (l) => l.leave_credits_id === leave.leave_credits_id,
        );

        return Number(leave.count) !== Number(original?.count);
      })
      .map((leave) => ({
        leave_credits_id: leave.leave_credits_id,
        employee_information_id: leave.employee_information_id,
        count: Number(leave.count), 
      }));

    // NO CHANGES
    if (updatedData.length === 0) {
      // console.log("No changes detected");
      setShowLeaveLog(false);
      return;
    }

    // HEADERS
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${accessToken}`);
    myHeaders.append("Content-Type", "application/json");

    // REQUEST BODY
    const raw = JSON.stringify({
      data: updatedData,
    });

    const requestOptions = {
      method: "PATCH",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    // API CALL
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/employees/leaveCredits/updateAllLeaveBalances`,
      requestOptions,
    );

    const text = await response.text();

    if (!response.ok) {
      console.error("API Error:", text);
      throw new Error("Failed to update leave balances");
    }

    let result;

    try {
      result = JSON.parse(text);
    } catch {
      result = text;
    }

    // console.log("Updated result:", result);

    setMessage("Leave Data Updated Successfully");
    setConfirmationPopup(true);
  } catch (err) {
    console.error("Update error:", err);

    setMessage(err.message || "Something went wrong");
    setConfirmationPopup(true);
  } finally {
    setSubmitting(false);
  }
};

 
  // console.log("Leave Count", leaveCount)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-[500px] max-h-[90vh] flex flex-col">
        {/* HEADER */}
        <div className="px-4 py-4 sticky top-0 bg-white z-10">
          <button
            onClick={() => setShowLeaveLog(false)}
            className="absolute right-4 top-4 text-gray-500"
          >
            ✕
          </button>

          <h2 className="text-xl font-semibold border-b pb-2">
            Leave Details
          </h2>

          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-gray-600">
              Name:{" "}
              <span className="text-base font-medium">{user.name}</span>
            </p>

            <p className="text-sm text-gray-600">
              Employee ID:{" "}
              <span className="text-base font-medium">
                {user.employeeId}
              </span>
            </p>
          </div>
        </div>

        {/* CONTENT */}
        <div className="p-4 overflow-y-auto">
          {loading ? (
            <div className="grid grid-cols-2 gap-3">
              {Array(6)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="border rounded-lg p-3 animate-pulse bg-gray-100 h-24"
                  />
                ))}
            </div>
          ) : (
            <Formik
              enableReinitialize
              initialValues={{
                leaveCounts: leaveCount || [],
              }}
              onSubmit={handleSubmit}
            >
              {({ values, isSubmitting }) => (
                <Form>
                  <h3 className="text-lg font-semibold mb-3">
                    Leave Summary
                  </h3>

                  <FieldArray name="leaveCounts">
                    {() => (
                      <div className="grid grid-cols-2 gap-3">
                        {values.leaveCounts?.map((leave, index) => {
                          const balance =
                            (Number(leave.count) || 0) -
                            (Number(leave.leave_days_taken) || 0);

                          return (
                            <div
                              key={leave.leave_credits_id}
                              className="border rounded-lg p-3"
                            >
                              <p className="font-medium mb-2 text-center">
                                {leave.LeaveType}
                              </p>

                              <div className="grid grid-cols-2 gap-2">
                                

                                {/* TAKEN */}
                                <div>
                                  <label className="text-xs">Leaves Taken</label>

                                  <Field
                                    name={`leaveCounts.${index}.leave_days_taken`}
                                    type="number"
                                    disabled
                                    className="w-full border rounded px-2 py-1 bg-gray-100"
                                  />
                                </div>

                                {/* BALANCE */}
                                <div>
                                  <label className="text-xs">Balance Count</label>

                                   <Field
                                    name={`leaveCounts.${index}.count`}
                                    type="number"
                                    className="w-full border rounded px-2 py-1"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </FieldArray>

                  {/* ACTION BUTTONS */}
                  <div className="mt-4 sticky bottom-0 bg-white pt-3 border-t flex gap-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary-hrms text-white py-2 rounded"
                    >
                      {isSubmitting ? "Updating..." : "Update"}
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowLeaveLog(false)}
                      className="w-full border py-2 rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>

      {/* SUCCESS / ERROR POPUP */}
      {confirmationPopup && message && (
        <DataSubmittedPopup
          message={message}
          setConfirmationPopup={setConfirmationPopup}
          updateTable={() => fetchLeaveCount(user.id)}
        />
      )}
    </div>
  );
};

export default LeaveLogPopup;